import React, { useState, useEffect } from 'react'
import { useBusiness } from '../../contexts/BusinessContext'
import { useSupabase } from '../../contexts/SupabaseContext'
import { formatDistanceToNow } from 'date-fns'
import { RefreshCw } from 'lucide-react'

interface ReceivedEmail {
  id: string
  from_email: string
  subject: string
  received_at: string
  read: boolean
  body: string
}

export function EmailInbox() {
  const { businessId } = useBusiness()
  const supabase = useSupabase()
  const [emails, setEmails] = useState<ReceivedEmail[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedEmail, setSelectedEmail] = useState<ReceivedEmail | null>(null)

  useEffect(() => {
    loadEmails()
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchNewEmails, 30000)
    return () => clearInterval(interval)
  }, [businessId])

  const loadEmails = async () => {
    if (!businessId) return

    try {
      const { data, error } = await supabase
        .from('received_emails')
        .select('*')
        .eq('business_id', businessId)
        .order('received_at', { ascending: false })
        .limit(20)

      if (error) throw error

      setEmails(data || [])
    } catch (error) {
      console.error('Error loading emails:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchNewEmails = async () => {
    if (!businessId || refreshing) return

    try {
      setRefreshing(true)
      
      // Call the edge function to fetch new emails
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/gmail-fetch-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId
        })
      })

      if (!response.ok) {
        throw new Error('Failed to fetch new emails')
      }

      // Reload emails from database
      await loadEmails()
    } catch (error) {
      console.error('Error fetching new emails:', error)
    } finally {
      setRefreshing(false)
    }
  }

  const markAsRead = async (emailId: string) => {
    try {
      const { error } = await supabase
        .from('received_emails')
        .update({ read: true })
        .eq('id', emailId)

      if (error) throw error

      // Update local state
      setEmails(emails.map(email => 
        email.id === emailId ? { ...email, read: true } : email
      ))
    } catch (error) {
      console.error('Error marking email as read:', error)
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-200 rounded mb-4"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Inbox</h3>
          <p className="text-sm text-gray-500">{emails.length} messages</p>
        </div>
        <button
          onClick={fetchNewEmails}
          disabled={refreshing}
          className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="divide-y">
        {emails.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No emails received yet
          </div>
        ) : (
          emails.map(email => (
            <div 
              key={email.id}
              className={`p-4 hover:bg-gray-50 cursor-pointer ${!email.read ? 'bg-blue-50' : ''}`}
              onClick={() => {
                setSelectedEmail(email === selectedEmail ? null : email)
                if (!email.read) markAsRead(email.id)
              }}
            >
              <div className="flex justify-between items-start mb-1">
                <span className="font-medium">{email.from_email}</span>
                <span className="text-sm text-gray-500">
                  {formatDistanceToNow(new Date(email.received_at), { addSuffix: true })}
                </span>
              </div>
              <div className="text-sm font-medium text-gray-900 mb-1">
                {email.subject || '(No subject)'}
              </div>
              {selectedEmail?.id === email.id && (
                <div className="mt-2 text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  {email.body}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
} 